/**
 * Admin Workflow Agent Config Page.
 * Simplified agent configuration for the single workflow builder agent per org.
 * Admins can create or edit the agent's name, description, model, system prompt, and MCP tools.
 */

import { useEffect, useMemo, useState } from 'react';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { Save, ExternalLink, Trash2, MoreVertical } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Modal } from '@/components/modal';
import {
  MultiSelectAutocomplete,
  type MultiSelectAutocompleteOption,
} from '@/components/ui/multi-select-autocomplete';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';
import {
  listChatAgents,
  createChatAgent,
  updateChatAgent,
  deleteChatAgent,
  getAvailableToolGroups,
} from '@/services/chat-agents/agents';
import type {
  ChatAgent,
  ChatAgentCreateRequest,
  ToolGroup,
} from '@/types/chat-agent-types';

const DEFAULT_TOOL_GROUPS = ['workflow-builder', 'composio'];

const AVAILABLE_MODELS = [
  { label: 'Claude Opus 4.6', value: 'claude-opus-4-6' },
  { label: 'Claude Sonnet 4.5', value: 'claude-sonnet-4-5-20250929' },
  { label: 'Claude Sonnet 4', value: 'claude-sonnet-4-20250514' },
  { label: 'Claude 3.7 Sonnet', value: 'claude-3-7-sonnet-20250219' },
  { label: 'Claude 3.5 Haiku', value: 'claude-3-5-haiku-20241022' },
];

const DEFAULT_SYSTEM_PROMPT =
  'You are a helpful assistant with access to MCP tools. Use them when appropriate to help the user. If a User asks for data visualization nodes you should make sure to try to use the D3 Data Visualization output node, the network graph output nodes, or make sure to use the Data Analysis agent type which can make artifacts when building workflows. You specialize in using workflow building tools. You should use opus 4.6 when building workflows with the builder tools. You should ALWAYS list nodes and inspect nodes before building to make a good decision about what to use. You should try to never use markdown as an output if a data visualization, spreadsheet, powerpoint is a better choice and you should use the appropriate node in the workflow. When a user asks for something that is not a workflow and a workflow could make the artifact, ask the user if they want to create a workflow. Also make sure to use rich visual outputs - if they ask for a dashboard, always use the d3 node or data analysis node for output.';

export function WorkflowAgentConfigPage() {
  const navigate = useOrgNavigate();
  const currentOrg = useOrgStore((s) => s.currentOrg);

  const [existingAgent, setExistingAgent] = useState<ChatAgent | null>(null);
  const [name, setName] = useState('Workflow Builder Agent');
  const [description, setDescription] = useState('');
  const [model, setModel] = useState('claude-opus-4-6');
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [selectedToolGroups, setSelectedToolGroups] =
    useState<string[]>(DEFAULT_TOOL_GROUPS);
  const [availableToolGroups, setAvailableToolGroups] = useState<ToolGroup[]>(
    [],
  );
  const [isLoadingTools, setIsLoadingTools] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isEditing = !!existingAgent;

  const toolGroupOptions = useMemo<MultiSelectAutocompleteOption[]>(
    () =>
      availableToolGroups
        .map((g) => ({
          value: g.id,
          label: g.label,
          description: `${g.tool_count} tools${g.category ? ` · ${g.category}` : ''}${g.description ? ` — ${g.description}` : ''}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [availableToolGroups],
  );

  // Load tool groups and existing agent in a single pass
  useEffect(() => {
    const init = async () => {
      if (!currentOrg?.orgid) return;
      setIsLoading(true);

      // 1. Fetch tool groups
      let loadedGroups: ToolGroup[] = [];
      try {
        loadedGroups = await getAvailableToolGroups(currentOrg.orgid);
        setAvailableToolGroups(loadedGroups);
        // Drop default groups the org doesn't have access to
        const groupIds = new Set(loadedGroups.map((g) => g.id));
        setSelectedToolGroups((prev) => prev.filter((id) => groupIds.has(id)));
      } catch (err) {
        console.warn('Failed to load tool groups');
        Sentry.captureException(err, {
          extra: { context: 'workflow-agent-config-load-tools' },
        });
      } finally {
        setIsLoadingTools(false);
      }

      // 2. Fetch existing agent for this org
      try {
        const agents = await listChatAgents(currentOrg.orgid);
        if (agents.length > 0) {
          // Select deterministically to avoid relying on backend ordering
          const sortedAgents = [...agents].sort((a, b) =>
            (a.id || '').localeCompare(b.id || ''),
          );
          if (sortedAgents.length > 1) {
            Sentry.captureMessage(
              'Multiple workflow agents found for org; using first by sorted id',
              { level: 'warning' },
            );
          }
          const agent = sortedAgents[0];
          setExistingAgent(agent);
          setName(agent.name);
          setDescription(agent.description || '');
          setModel(agent.model || 'claude-opus-4-6');
          setSystemPrompt(agent.system_prompt || DEFAULT_SYSTEM_PROMPT);
          const agentTools = agent.tools || [];
          const groups = loadedGroups
            .filter((g) => g.tools.some((t) => agentTools.includes(t)))
            .map((g) => g.id);
          if (groups.length > 0) {
            setSelectedToolGroups(groups);
          }
        }
      } catch (err) {
        setError('Failed to load agent configuration');
        Sentry.captureException(err, {
          extra: { context: 'workflow-agent-config-load' },
        });
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [currentOrg?.orgid]);

  const handleSave = async () => {
    if (!currentOrg?.orgid || !name.trim()) return;
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const selectedGroups = availableToolGroups.filter((g) =>
        selectedToolGroups.includes(g.id),
      );
      const selectedTools = selectedGroups.flatMap((g) => g.tools);

      const data: ChatAgentCreateRequest = {
        name: name.trim(),
        description: description.trim(),
        model,
        system_prompt: systemPrompt.trim(),
        tools: selectedTools,
      };

      if (isEditing && existingAgent) {
        await updateChatAgent(currentOrg.orgid, existingAgent.id, data);
        setSuccessMessage('Agent updated successfully.');
      } else {
        const created = await createChatAgent(currentOrg.orgid, data);
        if (created) {
          setExistingAgent(created);
          setSuccessMessage('Agent created successfully.');
        }
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to save agent';
      setError(message);
      Sentry.captureException(err, {
        extra: { context: 'workflow-agent-config-save' },
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentOrg?.orgid || !existingAgent) return;
    setIsDeleting(true);
    try {
      await deleteChatAgent(currentOrg.orgid, existingAgent.id);
      setExistingAgent(null);
      setName('Workflow Builder Agent');
      setDescription('');
      setModel('claude-opus-4-6');
      setSystemPrompt(DEFAULT_SYSTEM_PROMPT);
      setSelectedToolGroups(
        DEFAULT_TOOL_GROUPS.filter((id) =>
          availableToolGroups.some((g) => g.id === id),
        ),
      );
      setShowDeleteModal(false);
      setSuccessMessage('Agent deleted. You can create a new one.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
      Sentry.captureException(err, {
        extra: { context: 'workflow-agent-config-delete' },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className='mx-auto flex w-full max-w-2xl flex-col'>
        <div className='mb-8'>
          <Skeleton className='h-8 w-48' />
        </div>
        <div className='space-y-6'>
          <div className='space-y-2'>
            <Skeleton className='h-4 w-12' />
            <Skeleton className='h-10 w-full rounded-md' />
          </div>
          <div className='space-y-2'>
            <Skeleton className='h-4 w-24' />
            <Skeleton className='h-20 w-full rounded-md' />
          </div>
          <div className='space-y-2'>
            <Skeleton className='h-4 w-14' />
            <Skeleton className='h-10 w-52 rounded-md' />
          </div>
          <div className='space-y-2'>
            <Skeleton className='h-4 w-28' />
            <Skeleton className='h-36 w-full rounded-md' />
          </div>
          <div className='space-y-3'>
            <Skeleton className='h-4 w-20' />
            <Skeleton className='h-16 w-full rounded-lg' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex w-full max-w-2xl flex-col'>
      {/* Header */}
      <div className='mb-8 flex items-center justify-between'>
        <h1 className='text-2xl font-bold'>Workflow Builder Agent</h1>
        <div className='flex gap-3'>
          <Button
            onClick={handleSave}
            disabled={isSaving || !name.trim() || isLoadingTools}
          >
            <Save className='mr-2 size-4' />
            {isSaving
              ? 'Saving...'
              : isEditing
                ? 'Save Changes'
                : 'Create Agent'}
          </Button>
          {isEditing && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='icon'>
                  <MoreVertical className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={() => navigate(ROUTES.WORKFLOW_BUILDER)}
                >
                  <ExternalLink className='size-4 mr-2' />
                  Open Chat
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowDeleteModal(true)}>
                  <Trash2 className='size-4 mr-2' />
                  Delete Agent
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {error && (
        <div className='rounded-lg border border-destructive bg-destructive/10 p-4 mb-6'>
          <p className='text-sm text-destructive'>{error}</p>
        </div>
      )}

      {successMessage && (
        <div className='rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 p-4 mb-6'>
          <p className='text-sm text-green-700 dark:text-green-400'>
            {successMessage}
          </p>
        </div>
      )}

      <div className='space-y-6'>
        {/* Name */}
        <div className='space-y-2'>
          <Label htmlFor='name'>Name</Label>
          <Input
            id='name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Workflow Builder Agent'
            maxLength={100}
          />
        </div>

        {/* Description */}
        <div className='space-y-2'>
          <Label htmlFor='description'>Description</Label>
          <Textarea
            id='description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='What does this agent do?'
            rows={2}
            maxLength={500}
          />
        </div>

        {/* Model */}
        <div className='space-y-2'>
          <Label htmlFor='model'>Model</Label>
          <Select value={model} onValueChange={setModel}>
            <SelectTrigger>
              <SelectValue placeholder='Select a model' />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_MODELS.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* System Prompt */}
        <div className='space-y-2'>
          <Label htmlFor='system-prompt'>System Prompt</Label>
          <Textarea
            id='system-prompt'
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            placeholder='You are a helpful assistant...'
            rows={6}
            className='font-mono text-sm'
          />
        </div>

        {/* MCP Tools */}
        <div className='space-y-3'>
          <Label>MCP Tools</Label>
          <p className='text-xs text-muted-foreground'>
            Select which tool capabilities this agent should have access to.
          </p>
          {isLoadingTools ? (
            <div className='text-sm text-muted-foreground animate-pulse py-4'>
              Discovering available tools...
            </div>
          ) : availableToolGroups.length === 0 ? (
            <div className='text-sm text-muted-foreground py-4'>
              No MCP tool servers found. Check that mcp-servers-runner is
              running.
            </div>
          ) : (
            <MultiSelectAutocomplete
              value={selectedToolGroups}
              onChange={setSelectedToolGroups}
              options={toolGroupOptions}
              placeholder='Search MCP tool servers…'
              emptyMessage='No MCP tool servers found.'
              strict
            />
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isDialogOpen={showDeleteModal}
        setIsDialogOpen={setShowDeleteModal}
        title='Delete Workflow Builder Agent'
        description='This will permanently delete the agent and all its conversation history. This action cannot be undone.'
        footer={
          <div className='flex gap-3 justify-end'>
            <Button
              variant='outline'
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Agent'}
            </Button>
          </div>
        }
      >
        <p className='text-sm text-muted-foreground'>
          Are you sure you want to delete the workflow builder agent for this
          organization? Users will no longer be able to use the Workflow Builder
          chat until a new agent is configured.
        </p>
      </Modal>
    </div>
  );
}
